import { Loader } from '@crossfit/ui'
import IneligiblePage from 'pages/Ineligible'
import ReviewInvitePage from 'pages/TeamManagement/ReviewInvite'
import TermsOfService from 'components/TermsOfService'
import {
  canAccessAdminDirectoryPage,
  canAccessAdminContactInfoPage,
  canAccessAdminBillingAndInvoices,
  canAccessAdminCoachesAndStaffPage,
  canAccessAdminHelpPage,
  canAccessAdminContactUsPage,
  canAccessDashboard,
  canAccessPricing,
  canAccessProgramming,
  canAccessRoundtables,
  canAccessPlaybook,
  canAccessPartnerNetwork,
  canAccessContentKit,
  canAccessTeammates,
  canAccessAdmin,
  canAccessAffiliateStarterKit,
} from 'components/Auth/abilities'
import { useMyActiveSubscriptions } from 'hooks/subscriptions'
import { useSelector } from 'react-redux'
import { getMyAffiliates, getUser } from 'store/selectors'
import { useQuery } from 'react-query'
import * as agreementsService from 'services/agreements'
import { User } from 'types'

export const AccessPortal = (
  element: JSX.Element,
  path: string,
): JSX.Element => {
  const user = useSelector(getUser) as User
  const subscriptions = useMyActiveSubscriptions()
  const { selectedAffiliate } = useSelector(getMyAffiliates)
  const agreementQuery = useQuery(
    'agreement',
    () => agreementsService.fetchUserAgreement(user.id),
    {
      enabled: !!user?.id,
    },
  )
  const adminDirectoryPageAccess = canAccessAdminDirectoryPage(
    selectedAffiliate,
    subscriptions,
  )
  const adminContactInfoPageAccess = canAccessAdminContactInfoPage(
    selectedAffiliate,
    subscriptions,
  )
  const adminBillingAndInvoicesAccess = canAccessAdminBillingAndInvoices(
    selectedAffiliate,
    subscriptions,
  )
  const adminCoachesAndStaffPageAccess = canAccessAdminCoachesAndStaffPage(
    selectedAffiliate,
    subscriptions,
  )
  const adminHelpPageAccess = canAccessAdminHelpPage(
    selectedAffiliate,
    subscriptions,
  )
  const adminContactUsPageAccess = canAccessAdminContactUsPage(
    selectedAffiliate,
    subscriptions,
  )
  const dashboardAccess = canAccessDashboard(selectedAffiliate, subscriptions)
  const pricingAccess = canAccessPricing(selectedAffiliate, subscriptions)
  const programmingAccess = canAccessProgramming(
    selectedAffiliate,
    subscriptions,
  )
  const roundtablesAccess = canAccessRoundtables(
    selectedAffiliate,
    subscriptions,
  )
  const playbookAccess = canAccessPlaybook(selectedAffiliate, subscriptions)
  const partnerNetworkAccess = canAccessPartnerNetwork(
    selectedAffiliate,
    subscriptions,
  )
  const contentKitAccess = canAccessContentKit(selectedAffiliate, subscriptions)
  const starterKitAccess = canAccessAffiliateStarterKit(
    selectedAffiliate,
    subscriptions,
  )
  const teammatesAccess = canAccessTeammates(selectedAffiliate, subscriptions)
  const adminAccess = canAccessAdmin(selectedAffiliate, subscriptions)
  const hasValidAgreement = agreementQuery.data?.valid

  if (agreementQuery.isLoading || !subscriptions) {
    return <Loader />
  }

  if (hasValidAgreement) {
    if (path.includes('dashboard')) {
      if (dashboardAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('affiliate-pricing')) {
      if (pricingAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('programming')) {
      if (programmingAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('roundtables')) {
      if (roundtablesAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('playbook')) {
      if (playbookAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('partner-network')) {
      if (partnerNetworkAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('marketing')) {
      if (contentKitAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('starter-kit')) {
      if (starterKitAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('teammates')) {
      if (path.includes('teammates/review')) {
        return element
      }
      if (teammatesAccess) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('admin')) {
      if (adminAccess || location.pathname.includes('staff/review')) {
        return element
      }
      return <IneligiblePage />
    }

    if (path.includes('directory-page')) {
      if (adminDirectoryPageAccess) {
        return element
      }

      return <IneligiblePage />
    }

    if (path.includes('contact-info')) {
      if (adminContactInfoPageAccess) {
        return element
      }

      return <IneligiblePage />
    }

    if (path.includes('staff')) {
      if (path.includes('staff/review')) {
        return element
      }
      if (adminCoachesAndStaffPageAccess) {
        return element
      }

      return <IneligiblePage />
    }
  }

  if (path.includes('staff/review')) {
    return <ReviewInvitePage />
  }

  return <TermsOfService />
}
